import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import {
  CvApplyRequest,
  CvApplyResult,
  CvExtractionResult,
  CvImportLanguage,
  CvProfileDraft,
  CvStructureRequest,
} from '../shared/model/cv-import.model';

@Injectable({ providedIn: 'root' })
export class CvImportService {
  public resourceUrl = environment.apiConfig.uri + 'api/berater';

  constructor(private http: HttpClient) {}

  /** Uploads a .docx, .pdf or .pptx CV and returns the extracted text/structure. */
  extract(consultantId: string, file: File): Observable<CvExtractionResult> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<CvExtractionResult>(
      `${this.resourceUrl}/${consultantId}/cv-extract`,
      formData,
    );
  }

  /** Turns extracted CV text into a structured profile draft (LLM-based). */
  structure(
    consultantId: string,
    request: CvStructureRequest,
  ): Observable<CvProfileDraft> {
    return this.http.post<CvProfileDraft>(
      `${this.resourceUrl}/${consultantId}/cv-structure`,
      request,
    );
  }

  /** Full import flow: extract the uploaded CV, then structure the extracted text. */
  importCv(
    consultantId: string,
    file: File,
    language: CvImportLanguage,
  ): Observable<CvProfileDraft> {
    return this.extract(consultantId, file).pipe(
      switchMap((extraction) =>
        this.structure(consultantId, { text: extraction.text ?? '', language }),
      ),
    );
  }

  /**
   * Writes the approved sections into the profile via the lenient, non-destructive
   * cv-apply endpoint and returns what was skipped (NP20-874/875).
   */
  apply(consultantId: string, request: CvApplyRequest): Observable<CvApplyResult> {
    return this.http.post<CvApplyResult>(
      `${this.resourceUrl}/${consultantId}/cv-apply`,
      request,
    );
  }
}
